// ============================================================
// Pallipaarvai.service.ts
// RTK Query service for the pallipaarvai module.
// ============================================================
import { apiSlice } from '../../store/api/apiSlice';

// ── Types ────────────────────────────────────────────────────

export interface PallipaarvaiResponse<T> {
  dataStatus: boolean;
  message: string;
  result: T;
  records: T;
}

export interface BlockItem {
  blkId: number;
  blkName: string;
  distId: number;
}

// ── Endpoints ────────────────────────────────────────────────

export const pallipaarvaiApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // GET /ppGetAdminDrpDwns
    ppGetAdminDrpDwns: builder.query<PallipaarvaiResponse<unknown>, void>({
      query: () => ({
        url: 'ppGetAdminDrpDwns',
        method: 'GET',
        service: 'mobilephp',
      }),
    }),

    // GET block list from CloudFront JSON
    getBlockList: builder.query<BlockItem[], void>({
      queryFn: async () => {
        try {
          const response = await fetch(
            'https://d1wpyxz35bzzz4.cloudfront.net/sch_inspection_blkList.json'
          );
          const data: BlockItem[] = await response.json();
          return { data };
        } catch {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              error: 'Failed to fetch block list',
              data: undefined,
            },
          };
        }
      },
      keepUnusedDataFor: 300, // cache for 5 minutes to prevent duplicate calls
    }),
    // GET /ppBarChart
    ppBarChart: builder.query<PallipaarvaiResponse<unknown>, PpBarChartParams>({
      query: (params) => ({
        url: 'ppBarChart',
        method: 'GET',
        params,
        service: 'djangoTnEmis',
      }),
    }),

    // GET /ppVisitedCounts
    ppVisitedCounts: builder.query<
      PallipaarvaiResponse<unknown>,
      PpBarChartParams
    >({
      query: (params) => ({
        url: 'ppVisitedCounts',
        method: 'GET',
        params,
        service: 'djangoTnEmis',
      }),
    }),

    // GET /ppDashboardVisitedSchools
    ppDashboardVisitedSchools: builder.query<
      PallipaarvaiResponse<unknown>,
      PpDashboardVisitedSchoolsParams
    >({
      query: (params) => ({
        url: 'ppDashboardVisitedSchools',
        method: 'GET',
        params,
        service: 'djangoTnEmis',
      }),
    }),
  }),
  overrideExisting: false,
});

// ── Param Types ──────────────────────────────────────────────

export interface PpBarChartParams {
  acad_yr?: string;
  month_id?: string;
  district_id?: string;
  block_id?: string;
  category_id?: string;
  management_id?: string;
  designation_id?: string;
}

export interface PpDashboardVisitedSchoolsParams {
  acad_yr?: string;
  month_id?: string;
  district_id?: string;
  block_id?: string;
  category_id?: string;
  management_id?: string;
  designation_id?: string;
}

export const {
  usePpGetAdminDrpDwnsQuery,
  useGetBlockListQuery,
  usePpBarChartQuery,
  usePpVisitedCountsQuery,
  usePpDashboardVisitedSchoolsQuery,
} = pallipaarvaiApi;
